import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardHeader,
    Container,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TextField
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { saveAs } from 'file-saver';

function ProductWiseSales() {
    const [salesData, setSalesData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [itemName, setItemName] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    useEffect(() => {
        fetchSalesData();
    }, []);

    useEffect(() => {
        filterSalesData();
    }, [salesData, itemName, fromDate, toDate]);

    async function fetchSalesData() {
        try {
            const res = await axios.get('https://serveralgama.mylksystems.com/sales/productWise');
            if (res.data.length > 0) {
                setSalesData(res.data);
                setFilteredData(res.data);
            } else {
                toast.error("No sales records found!");
            }
        } catch (error) {
            console.error("Error fetching sales data", error);
        }
    }

    function filterSalesData() {
        let filtered = salesData;

        if (itemName) {
            filtered = filtered.filter(sale => sale.item_name.toLowerCase().includes(itemName.toLowerCase()));
        }

        if (fromDate) {
            filtered = filtered.filter(sale => new Date(sale.inv_date) >= new Date(fromDate));
        }

        if (toDate) {
            filtered = filtered.filter(sale => new Date(sale.inv_date) <= new Date(toDate));
        }

        setFilteredData(filtered);
    }

    function downloadCSV() {
        let csvContent =
            "Date,Item Name,Sold Quantity\n" +
            filteredData.map(sale => `${sale.inv_date},${sale.item_name},${sale.sold}`).join("\n");

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const today = new Date().toISOString().split('T')[0];
        saveAs(blob, `${today}_product_sales_report.csv`);
    }

    // Group sales data by date and item name
    const groupedData = {};
    filteredData.forEach(({ inv_date, item_name, sold }) => {
        if (!groupedData[inv_date]) {
            groupedData[inv_date] = {};
        }
        groupedData[inv_date][item_name] = sold;
    });

    // Extract unique product names (for clustered bars)
    const uniqueItems = [...new Set(filteredData.map(sale => sale.item_name))];

    // Convert grouped data to array format for Recharts
    const chartData = Object.keys(groupedData).map(date => {
        let obj = { date };
        uniqueItems.forEach(item => {
            obj[item] = groupedData[date][item] || 0;
        });
        return obj;
    });

    // Color palette for products
    const colors = ["#3f51b5", "#ff5722", "#4caf50", "#fbc02d", "#9c27b0"];

    return (
        <Container maxWidth={false}>
            <Card sx={{ padding: '15px' }}>
                <CardHeader title="Product-Wise Sales Report" />
                <Divider />
                <Box display="flex" gap={2} mt={2} mb={2}>
                    <TextField
                        label="Search Item Name"
                        variant="outlined"
                        size="small"
                        value={itemName}
                        onChange={(e) => setItemName(e.target.value)}
                    />
                    <TextField
                        label="From Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        size="small"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                    />
                    <TextField
                        label="To Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        size="small"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                    />
                    <Button variant="contained" color="primary" onClick={downloadCSV}>
                        Download CSV
                    </Button>
                </Box>
                {/* Product Sales Table */}
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Item Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Sold Quantity</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell>{row.inv_date}</TableCell>
                                    <TableCell>{row.item_name}</TableCell>
                                    <TableCell>{row.sold}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                {/* Product Sales Bar Chart */}
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={chartData}>
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        {uniqueItems.map((item, index) => (
                            <Bar key={item} dataKey={item} fill={colors[index % colors.length]} />
                        ))}
                    </BarChart>
                </ResponsiveContainer>

                <Divider sx={{ my: 3 }} />

            </Card>
        </Container>
    );
}

export default ProductWiseSales;
